import { z } from "zod";

// Risk Source Schema
export const RiskSourceSchema = z.object({
  url: z.string().url().optional(),
  thumbnail_url: z.string().url().optional(),
  location_description: z.string().optional(),
  name: z.string(),
});

export type RiskSource = z.infer<typeof RiskSourceSchema>;

// Chat Message Schema
export const ChatMessageSchema = z.object({
  message_id: z.string(),
  timestamp: z.string().datetime(),
  role: z.enum(["user", "assistant"]),
  type: z.enum(["message", "idk"]).default("message"),
  content: z.string(),
  metadata: z.record(z.any()).optional(),
});

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Research Thought Schema (new structure)
export const ResearchThoughtSchema = z.object({
  category: z.string(), // BRAND_ASSET, BRAND, COMPLIANCE, Locked
  tag: z.string(), // INFO, ALERT, WARNING, Locked
  message: z.string(),
});

export type ResearchThought = z.infer<typeof ResearchThoughtSchema>;

// Research Schema (new structure)
export const ResearchSchema = z.object({
  gathering: z.array(ResearchThoughtSchema),
  scanning: z.array(ResearchThoughtSchema),
});

export type Research = z.infer<typeof ResearchSchema>;

// Legacy Research Task Schema (for backward compatibility)
export const ResearchTaskSchema = z.object({
  task_id: z.string(),
  title: z.string(),
  thoughts: z.array(z.string()),
  risk_ids: z.array(z.string()).default([]),
  timestamp: z.string().datetime(),
});

export type ResearchTask = z.infer<typeof ResearchTaskSchema>;

// Risk Schema (new structure matching moar_results.json)
export const RiskSchema = z.object({
  id: z.string(), // Unique identifier for this risk
  priority: z.string(), // Low, Medium, High, Minor
  title: z.string(),
  description: z.string(),
  details: z.string(),
  evidence: z.string(),
  fix_effort: z.string(), // Low, Medium, High, Minor
  suggested_fix: z.string(),
  source_url: z.string(),
  image_url: z.string(),
});

export type Risk = z.infer<typeof RiskSchema>;

// Legacy Risk Schema (for backward compatibility)
export const LegacyRiskSchema = z.object({
  risk_id: z.string(),
  title: z.string(),
  text: z.string(),
  source: RiskSourceSchema,
  status: z.enum(["low", "medium", "high"]),
  trust_percentage: z.number().int().min(0).max(100),
  category: z.enum([
    "security",
    "reputation",
    "social_media",
    "news",
    "competitive",
    "industry",
  ]),
  metadata: z.record(z.any()).optional(),
  created_at: z.string().datetime(),
});

export type LegacyRisk = z.infer<typeof LegacyRiskSchema>;

// Risk Summary Schema
export const RiskSummarySchema = z.object({
  high: z.number().int().min(0),
  medium: z.number().int().min(0),
  low: z.number().int().min(0),
  total_risks: z.number().int().min(0),
  categories: z.record(z.number().int().min(0)).default({}),
});

export type RiskSummary = z.infer<typeof RiskSummarySchema>;

// BrandCheck Stats Schema
export const BrandCheckStatsSchema = z.object({
  total_sources_checked: z.number().int().min(0).default(0),
  issues_found: z.record(z.number().int().min(0)).default({}),
  categories_analyzed: z.array(z.string()).default([]),
  analysis_duration_seconds: z.number().optional(),
  confidence_score: z.number().min(0).max(100).optional(),
  last_updated: z.string().datetime(),
});

export type BrandCheckStats = z.infer<typeof BrandCheckStatsSchema>;

// API Request Schemas
export const SessionRequestSchema = z
  .object({
    email: z.string().email().optional(),
    company_name: z.string().optional(),
  })
  .refine((data) => data.email || data.company_name, {
    message: "Either email or company_name must be provided",
    path: ["email", "company_name"],
  });

export type SessionRequest = z.infer<typeof SessionRequestSchema>;

export const ChatRequestSchema = z.object({
  session_id: z.string(),
  risk_id: z.string().optional(),
  message: z.string().min(1),
});

export type ChatRequest = z.infer<typeof ChatRequestSchema>;

// Root-level BrandCheck Results Schema (new structure)
export const BrandCheckResultsSchema = z.object({
  domain: z.string(),
  research: ResearchSchema,
  results: z.array(RiskSchema),
  brand_context: z.string(),
  brand_guidelines: z.string(),
  analysis_complete: z.boolean().default(true),
});

export type BrandCheckResults = z.infer<typeof BrandCheckResultsSchema>;

// API Response Schemas
export const SessionResponseSchema = z.object({
  session_id: z.string(),
  email: z.string().email().optional().nullish(),
  company_name: z.string().optional().nullish(),
  status: z.enum(["ready", "created", "researching", "completed", "failed"]),
  created_at: z.string().datetime().optional().nullish(),
  message: z.string(),
});

export type SessionResponse = z.infer<typeof SessionResponseSchema>;

// Research Response Schema
export const ResearchMessageSchema = z.object({
  message_id: z.string(),
  timestamp: z.string().datetime(),
  step: z.string(),
  content: z.string(),
  status: z.enum(["in_progress", "completed", "failed"]).default("completed"),
  data: z.record(z.any()).optional(),
});

export type ResearchMessage = z.infer<typeof ResearchMessageSchema>;

// New Research Response Schema (matches new API structure)
export const ResearchResponseSchema = z.object({
  session_id: z.string(),
  status: z.enum(["created", "researching", "completed", "failed"]),
  research: ResearchSchema,
  brand_context: z.string().optional(),
  brand_guidelines: z.string().optional(),
});

export type ResearchResponse = z.infer<typeof ResearchResponseSchema>;

// Legacy Research Response Schema (for backward compatibility)
export const LegacyResearchResponseSchema = z.object({
  session_id: z.string(),
  status: z.enum(["created", "researching", "completed", "failed"]),
  tasks: z.array(ResearchTaskSchema),
  risk_summary: RiskSummarySchema.optional(),
  research_completed_at: z.string().datetime().optional(),
  total_tasks: z.number().int().min(0),
});

export type LegacyResearchResponse = z.infer<
  typeof LegacyResearchResponseSchema
>;

// New Results Response Schema (matches new API structure)
export const ResultsResponseSchema = z.object({
  session_id: z.string(),
  status: z.enum(["created", "researching", "completed", "failed"]),
  domain: z.string(),
  results: z.array(RiskSchema),
  brand_context: z.string().optional(),
  brand_guidelines: z.string().optional(),
  analysis_complete: z.boolean().default(true),
});

export type ResultsResponse = z.infer<typeof ResultsResponseSchema>;

// Legacy Results Response Schema (for backward compatibility)
export const LegacyResultsResponseSchema = z.object({
  session_id: z.string(),
  status: z.enum(["created", "researching", "completed", "failed"]),
  risks: z.array(LegacyRiskSchema),
  risks_generated_at: z.string().datetime().optional(),
  total_risks: z.number().int().min(0),
  summary: z.object({
    total: z.number().int().min(0),
    by_status: z.record(z.number().int().min(0)),
    by_category: z.record(z.number().int().min(0)),
  }),
  chat_suggestions: z.array(z.string()).default([]),
});

export type LegacyResultsResponse = z.infer<typeof LegacyResultsResponseSchema>;

// Chat Response Schema
export const ChatResponseSchema = z.object({
  session_id: z.string(),
  message: z.string(),
});

export type ChatResponse = z.infer<typeof ChatResponseSchema>;

// Health Response Schema
export const HealthResponseSchema = z.object({
  service: z.string(),
  status: z.enum(["healthy", "degraded", "unhealthy"]),
  version: z.string(),
  endpoints: z.array(z.string()),
});

export type HealthResponse = z.infer<typeof HealthResponseSchema>;

// Error Response Schema
export const ErrorResponseSchema = z.object({
  detail: z.string().optional(),
  error: z.string().optional(),
  message: z.string().optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// BrandCheck Session Schema (complete document with new structure)
export const BrandCheckSessionSchema = z.object({
  _id: z.string().optional(), // MongoDB ObjectId as string
  email: z.string().email().optional(),
  company_name: z.string().optional(),
  domain: z.string().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  status: z.enum(["created", "researching", "completed", "failed"]),

  // New root-level analysis results
  analysis_results: BrandCheckResultsSchema.optional(),

  // Legacy fields for backward compatibility
  research_tasks: z.array(z.record(z.any())).default([]), // Generic objects for legacy tasks
  research_completed_at: z.string().datetime().optional(),
  risk_summary: RiskSummarySchema.optional(),
  risks: z.array(z.record(z.any())).default([]), // Generic objects for legacy risks
  risks_generated_at: z.string().datetime().optional(),
  chat_suggestions: z.array(z.string()).default([]),
  brand_context: z.string().optional(), // Legacy field
  brand_guidelines: z.string().optional(), // Legacy field

  // Common fields
  chat_messages: z.array(ChatMessageSchema).default([]),
  stats: BrandCheckStatsSchema.optional(),
  context: z.record(z.any()).default({}),
  user_agent: z.string().optional(),
  ip_address: z.string().optional(),
  session_metadata: z.record(z.any()).optional(),
});

export type BrandCheckSession = z.infer<typeof BrandCheckSessionSchema>;

// Utility schemas for frontend state management
export const BrandCheckProgressSchema = z.object({
  session_id: z.string(),
  currentStep: z.enum(["session", "research", "results", "chat"]),
  isLoading: z.boolean().default(false),
  error: z.string().optional(),
  hasStartedResearch: z.boolean().default(false),
  hasCompletedResearch: z.boolean().default(false),
  hasGeneratedResults: z.boolean().default(false),
});

export type BrandCheckProgress = z.infer<typeof BrandCheckProgressSchema>;

export const BrandCheckStateSchema = z.object({
  session: SessionResponseSchema.optional(),
  research: ResearchResponseSchema.optional(),
  results: ResultsResponseSchema.optional(),
  chatHistory: z.array(ChatMessageSchema).default([]),
  progress: BrandCheckProgressSchema.optional(),
  lastUpdated: z.string().datetime().optional(),
});

export type BrandCheckState = z.infer<typeof BrandCheckStateSchema>;
